import axios from 'axios';
import authService from '../services/authService';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://backend.saltmarshdigital.com/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    
    // Add detailed logging
    console.log('API Request:', {
      method: config.method,
      url: config.url,
      fullUrl: `${config.baseURL}${config.url}`,
      hasToken: !!token,
      tokenLength: token ? token.length : 0,
      tokenPrefix: token ? token.substring(0, 15) : null,
      headers: config.headers,
    });
    
    // Only set Content-Type to application/json if not multipart/form-data
    const contentType = config.headers['Content-Type'];
    if ((config.method?.toLowerCase() === 'post' || config.method?.toLowerCase() === 'put') &&
        (!contentType || (typeof contentType === 'string' && !contentType.includes('multipart/form-data')))) {
      config.headers['Content-Type'] = 'application/json';
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Authorization header added:', `Bearer ${token.substring(0, 10)}...`);
    } else {
      console.warn('No authentication token found in localStorage');
    }
    
    // Add app ID header if available from environment
    const appId = process.env.REACT_APP_APP_ID || '1';
    config.headers['X-App-ID'] = appId;
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for handling common errors
apiClient.interceptors.response.use(
  (response) => {
    console.log('API Response:', {
      status: response.status,
      url: response.config.url,
      fullUrl: `${response.config.baseURL}${response.config.url}`,
      success: response.data?.success,
      data: response.data,
    });
    
    return response;
  },
  async (error) => {
    console.error('API Response Error:', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
      url: error.config?.url,
      fullUrl: error.config ? `${error.config.baseURL}${error.config.url}` : null,
      headers: error.config?.headers,
    });
    
    // Handle 401 Unauthorized errors (token expired or invalid)
    if (error.response && error.response.status === 401) {
      // If we're already at the login page, don't redirect again
      if (!window.location.pathname.includes('/login')) {
        console.warn('Authentication failed, redirecting to login');
        // Clear token
        localStorage.removeItem('token');
        
        // Redirect to login
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }

    // Auto-fix 403 errors for course access by adding the user to the app
    if (error.response && error.response.status === 403) {
      const url = error.config?.url || '';
      
      // Check if this is a course-related endpoint
      if (url.includes('/available-courses') || url.includes('/courses/')) {
        try {
          console.log('Attempting to auto-fix course access...');
          
          // Get the app ID from environment or use default
          const appId = process.env.REACT_APP_APP_ID || '1';
          
          // Try to join the app using our comprehensive method
          await authService.joinApp(appId);
          console.log('User added to app successfully, retrying original request...');
          
          // Retry the original request
          const originalRequest = error.config;
          return axios(originalRequest);
        } catch (fixError) {
          console.error('Failed to auto-fix course access:', fixError);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export default apiClient; 